import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import Product from "./pages/Product";
import About from "./pages/About";
import NotFound from "./pages/NotFound";
import RequestDemo from "./pages/RequestDemo";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CleanTechEarlyAccess from "./pages/CleanTechEarlyAccess";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ReportPost from "./pages/ReportPost";

import LAResRebuildingCup from "./pages/LAResRebuildingCup";
import REPlusConference from "./pages/REPlusConference";
import GetStarted from "./pages/GetStarted";
import IBS2026 from "./pages/IBS2026";
import Onboarding from "./pages/Onboarding";
import CatalyzeSample from "./pages/samples/CatalyzeSample";
import CitadelSample from "./pages/samples/CitadelSample";
import ChargeSample from "./pages/samples/ChargeSample";
import PivotSample from "./pages/samples/PivotSample";
import SimplySolarSample from "./pages/samples/SimplySolarSample";
import VerdeSample from "./pages/samples/VerdeSample";
import AltusPowerSample from "./pages/samples/AltusPowerSample";
import SolarLandscapeSample from "./pages/samples/SolarLandscapeSample";
import SolarOptimumSample from "./pages/samples/SolarOptimumSample";
import SunVestSample from "./pages/samples/SunVestSample";
import DsdRenewablesSample from "./pages/samples/DsdRenewablesSample";
import PowerflexSample from "./pages/samples/PowerflexSample";
import OnyxRenewablesSample from "./pages/samples/OnyxRenewablesSample";
import MarsEnergyGroupSample from "./pages/samples/MarsEnergyGroupSample";
import ParadiseEnergySolutionsSample from "./pages/samples/ParadiseEnergySolutionsSample";
import ParadiseEnergySolutionsCommercialSample from "./pages/samples/ParadiseEnergySolutionsCommercialSample";
import EnerfloSample from "./pages/samples/EnerfloSample";
import PermitPowerSample from "./pages/samples/PermitPowerSample";
import FreedomPowerSample from "./pages/samples/FreedomPowerSample";
import CoreDevelopmentGroupSample from "./pages/samples/CoreDevelopmentGroupSample";
import VerogySample from "./pages/samples/VerogySample";


import CaseStudyPost from "./pages/CaseStudyPost";
import CommercialCleanEnergyArticle from "./pages/resources/CommercialCleanEnergyArticle";
import ResidentialCleanEnergyArticle from "./pages/resources/ResidentialCleanEnergyArticle";



// ScrollToTop component that will handle scrolling to the top on route changes
// and push an SPA pageview event to GTM/GA on every route change.
const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: 'pageview',
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Helmet>
            <title>Fordje - Build Without Surprises</title>
            <meta name="description" content="Fordje delivers complete, accurate regulatory data across every U.S. city — automatically mapped and always up to date" />
          </Helmet>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/product" element={<Product />} />
            <Route path="/about" element={<About />} />
            <Route path="/solutions/residential-builders" element={<Navigate to="/" replace />} />
            <Route path="/solutions/clean-tech" element={<Navigate to="/product" replace />} />
            <Route path="/solutions/commercial" element={<Navigate to="/product" replace />} />
            <Route path="/request-demo" element={<RequestDemo />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/clean-tech-early-access" element={<Navigate to="/" replace />} />
            
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/report/discovery-tax" element={<CaseStudyPost />} />
            <Route path="/report/:slug" element={<ReportPost />} />
            {/* Page kept in source for future reference, but not public */}
            <Route path="/fordje-vs-upcodes" element={<Navigate to="/" replace />} />
            <Route path="/la-resilient-rebuilding-cup" element={<LAResRebuildingCup />} />
            <Route path="/re-plus-conference" element={<Navigate to="/" replace />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/ibs-2026" element={<IBS2026 />} />
            <Route path="/try-it" element={<Onboarding />} />
            <Route path="/samples/catalyze" element={<CatalyzeSample />} />
            <Route path="/samples/freedom-power" element={<FreedomPowerSample />} />
            <Route path="/samples/core-development-group" element={<CoreDevelopmentGroupSample />} />
            <Route path="/samples/verogy" element={<VerogySample />} />


            <Route path="/samples/citadel" element={<CitadelSample />} />
            <Route path="/samples/charge" element={<ChargeSample />} />
            <Route path="/samples/pivot" element={<PivotSample />} />
            <Route path="/samples/simply-solar" element={<SimplySolarSample />} />
            <Route path="/samples/verde-solutions" element={<VerdeSample />} />
            <Route path="/samples/altus-power" element={<AltusPowerSample />} />
            <Route path="/samples/solar-landscape" element={<SolarLandscapeSample />} />
            <Route path="/samples/solar-optimum" element={<SolarOptimumSample />} />
            <Route path="/samples/sunvest-solar" element={<SunVestSample />} />
            <Route path="/samples/dsd-renewables" element={<DsdRenewablesSample />} />
            <Route path="/samples/powerflex" element={<PowerflexSample />} />
            <Route path="/samples/onyx-renewables" element={<OnyxRenewablesSample />} />
            <Route path="/samples/mars-energy-group" element={<MarsEnergyGroupSample />} />
            <Route path="/samples/paradise-energy-solutions" element={<ParadiseEnergySolutionsSample />} />
            <Route path="/samples/paradise-energy-solutions-commercial" element={<ParadiseEnergySolutionsCommercialSample />} />
            <Route path="/samples/enerflo" element={<EnerfloSample />} />
            <Route path="/samples/permit-power" element={<PermitPowerSample />} />



            {/* Commercial Clean-Energy Guide cluster (AEO resources) */}
            <Route path="/commercial-clean-energy-guide" element={<CommercialCleanEnergyArticle articleKey="pillar" />} />
            <Route path="/commercial-clean-energy-guide/expanding-to-a-new-market" element={<CommercialCleanEnergyArticle articleKey="expanding" />} />
            <Route path="/commercial-clean-energy-guide/confirm-jurisdiction-allows-project" element={<CommercialCleanEnergyArticle articleKey="confirm" />} />
            <Route path="/commercial-clean-energy-guide/solar" element={<CommercialCleanEnergyArticle articleKey="solar" />} />
            <Route path="/commercial-clean-energy-guide/battery-storage" element={<CommercialCleanEnergyArticle articleKey="battery-storage" />} />
            <Route path="/commercial-clean-energy-guide/ev-charging" element={<CommercialCleanEnergyArticle articleKey="ev-charging" />} />
            <Route path="/commercial-clean-energy-guide/chatgpt-vs-code-research" element={<CommercialCleanEnergyArticle articleKey="chatgpt" />} />

            {/* Redirects from old pillar path */}
            <Route path="/commercial-clean-energy-project-requirements" element={<Navigate to="/commercial-clean-energy-guide" replace />} />
            <Route path="/commercial-clean-energy-project-requirements/expanding-to-a-new-market" element={<Navigate to="/commercial-clean-energy-guide/expanding-to-a-new-market" replace />} />
            <Route path="/commercial-clean-energy-project-requirements/confirm-jurisdiction-allows-project" element={<Navigate to="/commercial-clean-energy-guide/confirm-jurisdiction-allows-project" replace />} />
            <Route path="/commercial-clean-energy-project-requirements/solar" element={<Navigate to="/commercial-clean-energy-guide/solar" replace />} />
            <Route path="/commercial-clean-energy-project-requirements/battery-storage" element={<Navigate to="/commercial-clean-energy-guide/battery-storage" replace />} />
            <Route path="/commercial-clean-energy-project-requirements/ev-charging" element={<Navigate to="/commercial-clean-energy-guide/ev-charging" replace />} />
            <Route path="/commercial-clean-energy-project-requirements/chatgpt-vs-code-research" element={<Navigate to="/commercial-clean-energy-guide/chatgpt-vs-code-research" replace />} />

            {/* Residential Clean-Energy Guide cluster (AEO resources) */}
            <Route path="/residential-clean-energy-guide" element={<ResidentialCleanEnergyArticle articleKey="pillar" />} />
            <Route path="/residential-clean-energy-guide/ahj-database" element={<ResidentialCleanEnergyArticle articleKey="ahj-database" />} />
            <Route path="/residential-clean-energy-guide/permitting" element={<ResidentialCleanEnergyArticle articleKey="permitting" />} />
            <Route path="/residential-clean-energy-guide/utility-interconnection-pto" element={<ResidentialCleanEnergyArticle articleKey="utility-interconnection-pto" />} />
            <Route path="/residential-clean-energy-guide/chatgpt-vs-code-research" element={<ResidentialCleanEnergyArticle articleKey="chatgpt" />} />

            {/* Redirects from the handoff's original residential path */}
            <Route path="/residential-clean-energy-project-requirements" element={<Navigate to="/residential-clean-energy-guide" replace />} />
            <Route path="/residential-clean-energy-project-requirements/ahj-database" element={<Navigate to="/residential-clean-energy-guide/ahj-database" replace />} />
            <Route path="/residential-clean-energy-project-requirements/permitting" element={<Navigate to="/residential-clean-energy-guide/permitting" replace />} />
            <Route path="/residential-clean-energy-project-requirements/utility-interconnection-pto" element={<Navigate to="/residential-clean-energy-guide/utility-interconnection-pto" replace />} />
            <Route path="/residential-clean-energy-project-requirements/chatgpt-vs-code-research" element={<Navigate to="/residential-clean-energy-guide/chatgpt-vs-code-research" replace />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}

            {/* Legacy / removed routes — redirect to home so Google drops the soft-404s */}
            <Route path="/beta" element={<Navigate to="/" replace />} />
            <Route path="/pricing" element={<Navigate to="/" replace />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/developers" element={<Navigate to="/" replace />} />
            <Route path="/contact-us" element={<Navigate to="/request-demo" replace />} />
            <Route path="/contact" element={<Navigate to="/request-demo" replace />} />
            <Route path="/demo" element={<Navigate to="/request-demo" replace />} />
            <Route path="/book-a-demo" element={<Navigate to="/request-demo" replace />} />
            <Route path="/solutions" element={<Navigate to="/product" replace />} />
            <Route path="/features" element={<Navigate to="/product" replace />} />
            <Route path="/resources" element={<Navigate to="/blog" replace />} />
            <Route path="/insights" element={<Navigate to="/blog" replace />} />
            <Route path="/case-studies" element={<Navigate to="/report/discovery-tax" replace />} />
            <Route path="/team" element={<Navigate to="/about" replace />} />
            <Route path="/careers" element={<Navigate to="/about" replace />} />
            <Route path="/about-us" element={<Navigate to="/about" replace />} />

            <Route path="*" element={<NotFound />} />

          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
